import type { TNode } from '../Node';
import type { SelectionDescendantMap } from '../useTreeApi';

const insertDescendantSelections = ({
  selectionDescendantMap,
  ancestors,
}: {
  selectionDescendantMap: SelectionDescendantMap;
  ancestors: string[];
}): SelectionDescendantMap => {
  if (ancestors.length === 0) return selectionDescendantMap;

  const [id, ...rest] = ancestors;
  // @ts-expect-error noUncheckedIndexedAccess
  if (!selectionDescendantMap.has(id)) {
    // @ts-expect-error noUncheckedIndexedAccess
    selectionDescendantMap.set(id, new Map());
  }

  return selectionDescendantMap.set(
    // @ts-expect-error noUncheckedIndexedAccess
    id,
    insertDescendantSelections({
      selectionDescendantMap: selectionDescendantMap.get(
        // @ts-expect-error noUncheckedIndexedAccess
        id
      ) as SelectionDescendantMap,
      ancestors: rest,
    })
  );
};

const removeDescendantSelections = ({
  selectionDescendantMap,
  ancestors,
  selectedIds,
}: {
  selectionDescendantMap: SelectionDescendantMap;
  ancestors: string[];
  selectedIds: string[];
}): SelectionDescendantMap => {
  if (ancestors.length === 1) {
    // @ts-expect-error noUncheckedIndexedAccess
    const noOfChildren = selectionDescendantMap.get(ancestors[0])?.size || 0;
    if (noOfChildren > 0) return selectionDescendantMap;
    else {
      // @ts-expect-error noUncheckedIndexedAccess
      selectionDescendantMap.delete(ancestors[0]);
      return selectionDescendantMap;
    }
  } else {
    const [id, ...rest] = ancestors;
    const newSelectionMap = removeDescendantSelections({
      // @ts-expect-error noUncheckedIndexedAccess
      selectionDescendantMap: selectionDescendantMap.get(id) || new Map(),
      ancestors: rest,
      selectedIds,
    });
    // @ts-expect-error noUncheckedIndexedAccess
    if (newSelectionMap.size === 0 && !selectedIds.includes(id)) {
      // @ts-expect-error noUncheckedIndexedAccess
      selectionDescendantMap.delete(id);
      return selectionDescendantMap;
      // @ts-expect-error noUncheckedIndexedAccess
    } else return selectionDescendantMap.set(id, newSelectionMap);
  }
};

// One of node or ancestors must be provided
type UpdateDescendantSelectionsBaseProps =
  | {
      node: TNode;
      selectionDescendantMap: SelectionDescendantMap;
      ancestors?: string[];
    }
  | {
      node?: undefined;
      selectionDescendantMap: SelectionDescendantMap;
      ancestors: string[];
    };

type InsertDescendantSelectionsProps = UpdateDescendantSelectionsBaseProps & {
  operation: 'INSERT';
};

type RemoveDescendantSelectionsProps = UpdateDescendantSelectionsBaseProps & {
  operation: 'REMOVE';
  selectedIds: string[];
};

type UpdateDescendantSelectionsProps =
  | InsertDescendantSelectionsProps
  | RemoveDescendantSelectionsProps;

export const updateDescendantSelections = (
  props: UpdateDescendantSelectionsProps
): string[] => {
  const { operation, node, selectionDescendantMap } = props;

  // If node doesn't exist, props.ancestors *must* exist (enforced by type)
  const ancestors = node ? getAncestors(node) : props.ancestors;

  switch (operation) {
    case 'INSERT':
      return getIndeterminateParents(
        insertDescendantSelections({ selectionDescendantMap, ancestors })
      );
    case 'REMOVE':
      return getIndeterminateParents(
        removeDescendantSelections({
          selectionDescendantMap,
          ancestors,
          selectedIds: props.selectedIds,
        })
      );
  }
};

const getAncestors = (node: TNode): string[] => {
  if (node.parent) return [...getAncestors(node.parent), node.id];
  return [];
};

const getIndeterminateParents = (
  selectionDescendantMap: SelectionDescendantMap
): string[] => {
  const indeterminateParents: string[] = [];
  selectionDescendantMap.forEach((value, key) => {
    if (value.size === 0) return;
    else {
      indeterminateParents.push(key);
      indeterminateParents.push(...getIndeterminateParents(value));
    }
  });
  return indeterminateParents;
};
